import { RapidWorkflowDataType } from "@/components/flow/shared/Deploy/documentation/types";
import { FrameInferenceResult } from "@/types/inference";
import { db } from "@/firebase/app";
import { doc, getDoc } from "firebase/firestore";
import { RapidStateCreator } from "../types";

export type ExampleData = Array<{
    isVideo: boolean;
    filePath: string;
    resizedFilePath?: string | null;
    isSharable: boolean;

    authenticatedData?: { url: string; resizedUrl?: string };
}>;

// Build metadata inference types - tracks which inference type was used for each class
export enum BuildInferenceType {
    SAM3 = "sam3",
    OWLV2 = "owlv2"
}

export type BuildMetadataInferenceTypes = Record<string, BuildInferenceType>;

export type BuildMetadata = { inferenceTypes: BuildMetadataInferenceTypes };

export type CreateRapidWorkflowProps = {
    workspaceId?: string;
    task: string | null;
    groundTruthData: FrameInferenceResult[];
    isSync?: boolean;
    exampleData: ExampleData;
    buildMetadata: BuildMetadata;
};

export type CreateRapidWorkflowSuccessCB = (workflow: RapidWorkflowDataType | null) => void;
export type CreateRapidWorkflowApiErrorCB = (e: Error) => void;
export type CreateRapidWorkflowResult = {
    workflow: RapidWorkflowDataType | null;
    status: string;
    error: Error | null;
};
export type CreateRapidWorkflow = (
    props: CreateRapidWorkflowProps,
    api: any
) => Promise<CreateRapidWorkflowResult>;

export interface WorkflowSliceState {
    workflow: RapidWorkflowDataType | null;
    creatingRapidWorkflow: boolean;
    workflowLoading: boolean;
    workflowError: string | null;
    workflowToken: string | null;
    tokenLoading: boolean;
    tokenError: string | null;

    // Getters
    getCurrentWorkflow: () => RapidWorkflowDataType | null;
    getWorkflowUrl: () => string;
    getWorkflowToken: () => string | null;
    isWorkflowCreating: () => boolean;
    isTokenLoading: () => boolean;

    // Setters
    setWorkflow: (workflow: RapidWorkflowDataType | null) => void;
    setCreatingRapidWorkflow: (creating: boolean) => void;
    setWorkflowLoading: (loading: boolean) => void;
    setWorkflowError: (error: string | null) => void;
    setWorkflowToken: (token: string | null) => void;
    setTokenLoading: (loading: boolean) => void;
    setTokenError: (error: string | null) => void;

    // Actions
    createRapidWorkflow: CreateRapidWorkflow;
    fetchWorkflowByUrl: (
        workflowUrl: string,
        workspaceId: string,
        api: any,
        onSuccess?: CreateRapidWorkflowSuccessCB,
        onError?: CreateRapidWorkflowApiErrorCB
    ) => Promise<void>;
    fetchPublicWorkflowBySlug: (
        shareableSlug: string,
        api: any,
        onSuccess?: CreateRapidWorkflowSuccessCB,
        onError?: CreateRapidWorkflowApiErrorCB
    ) => Promise<void>;
    fetchWorkflowById: (id: string) => Promise<void>;
    fetchWorkflowToken: (workspaceId: string, api: any) => Promise<void>;
    clearWorkflowState: () => void;
}

let creatingRapidWorkflowPromise: null | Promise<any> = null;

export const createWorkflowSlice: RapidStateCreator<WorkflowSliceState> = (set, get) => ({
    // Initial state
    workflow: null as RapidWorkflowDataType | null,
    creatingRapidWorkflow: false,
    workflowLoading: false,
    workflowError: null,
    workflowToken: null,
    tokenLoading: false,
    tokenError: null,

    // Getters
    getCurrentWorkflow: () => get().workflow,
    getWorkflowUrl: () => get().workflow?.url || "",
    getWorkflowToken: () => get().workflowToken,
    isWorkflowCreating: () => get().creatingRapidWorkflow,
    isTokenLoading: () => get().tokenLoading,

    // Setters
    setWorkflow: (workflow: RapidWorkflowDataType | null) => {
        set({ workflow }, false, "setWorkflow");

        // Auto-stop creating workflow when URL becomes available
        if (workflow?.url && get().creatingRapidWorkflow) {
            set({ creatingRapidWorkflow: false }, false, "setWorkflow:stopCreating");
        }
    },

    setCreatingRapidWorkflow: (creatingRapidWorkflow: boolean) =>
        set({ creatingRapidWorkflow }, false, "setCreatingRapidWorkflow"),

    setWorkflowLoading: (workflowLoading: boolean) =>
        set({ workflowLoading }, false, "setWorkflowLoading"),

    setWorkflowError: (workflowError: string | null) =>
        set({ workflowError }, false, "setWorkflowError"),

    setWorkflowToken: (workflowToken: string | null) =>
        set({ workflowToken }, false, "setWorkflowToken"),

    setTokenLoading: (tokenLoading: boolean) => set({ tokenLoading }, false, "setTokenLoading"),

    setTokenError: (tokenError: string | null) => set({ tokenError }, false, "setTokenError"),

    // Actions
    createRapidWorkflow: async (props: CreateRapidWorkflowProps, api: any) => {
        const { groundTruthData, workspaceId, task, isSync, exampleData, buildMetadata } = props;

        if (!workspaceId) {
            throw new Error("No current workspace");
        }

        if (get().creatingRapidWorkflow) {
            if (!creatingRapidWorkflowPromise) {
                throw new Error("creatingRapidWorkflow is in Zustand while promise is null");
            }
            const workflow = await creatingRapidWorkflowPromise;
            creatingRapidWorkflowPromise = null;
            return { workflow, status: "success", error: null } as CreateRapidWorkflowResult;
        }

        if (!get().creatingRapidWorkflow) {
            set(
                { workflow: null, workflowError: null, creatingRapidWorkflow: true },
                false,
                "createRapidWorkflow:start"
            );
        }

        try {
            set({ workflowLoading: true }, false, "createRapidWorkflow:loading");

            creatingRapidWorkflowPromise = api.core.createRapidWorkflow({
                groundTruthData,
                workspaceId,
                task,
                isSync,
                exampleData,
                buildMetadata
            });
            const workflow = await creatingRapidWorkflowPromise;
            creatingRapidWorkflowPromise = null;

            set(
                { workflow, workflowLoading: false, workflowError: null },
                false,
                "createRapidWorkflow:success"
            );

            // Auto-fetch token when workflow is created
            if (workflow?.id && workspaceId) {
                await get().fetchWorkflowToken(workspaceId, api);
            }

            // Auto-navigate to deploy step when workflow is created
            if (workflow?.url) {
                set({ creatingRapidWorkflow: false }, false, "createRapidWorkflow:complete");
                return { workflow, status: "success", error: null } as CreateRapidWorkflowResult;
            } else {
                throw new Error("Failed to create workflow -- no URL");
            }
        } catch (error) {
            console.error("🐞 createRapidWorkflow", "error", error);
            const errorMessage =
                error instanceof Error ? error.message : "Failed to create workflow";
            set(
                {
                    workflow: null,
                    workflowLoading: false,
                    workflowError: errorMessage,
                    creatingRapidWorkflow: false
                },
                false,
                "createRapidWorkflow:error"
            );

            return {
                workflow: null,
                status: "error",
                error: new Error(errorMessage)
            } as CreateRapidWorkflowResult;
        }
    },

    fetchWorkflowByUrl: async (
        workflowUrl: string,
        workspaceId: string,
        api: any,
        onSuccess?: (workflow: RapidWorkflowDataType | null) => void,
        onError?: (e: Error) => void
    ) => {
        try {
            set({ workflowLoading: true, workflowError: null }, false, "fetchWorkflowByUrl:start");

            const workflow = await api.core.getRapidWorkflow(workflowUrl, workspaceId);

            set({ workflow, workflowLoading: false }, false, "fetchWorkflowByUrl:success");

            // Auto-fetch token when workflow is loaded
            if (workflow?.id && workspaceId) {
                await get().fetchWorkflowToken(workspaceId, api);
            }

            if (onSuccess) onSuccess(workflow);

            return workflow;
        } catch (error) {
            console.error("Error fetching workflow by URL:", error);
            const errorMessage =
                error instanceof Error ? error.message : "Failed to fetch workflow";
            set(
                { workflow: null, workflowError: errorMessage, workflowLoading: false },
                false,
                "fetchWorkflowByUrl:error"
            );

            if (onError) onError(new Error(errorMessage));
        }
    },

    fetchPublicWorkflowBySlug: async (
        shareableSlug: string,
        api: any,
        onSuccess?: (workflow: RapidWorkflowDataType | null) => void,
        onError?: (e: Error) => void
    ) => {
        try {
            set(
                { workflowLoading: true, workflowError: null },
                false,
                "fetchPublicWorkflowBySlug:start"
            );

            const response = await api.core.getPublicRapidWorkflowBySlug(shareableSlug);
            const workflow = response.data || response;

            set({ workflow, workflowLoading: false }, false, "fetchPublicWorkflowBySlug:success");

            // Public workflows don't need tokens
            // Just set a null token to satisfy any checks
            set({ workflowToken: null }, false, "fetchPublicWorkflowBySlug:noToken");

            if (onSuccess) onSuccess(workflow);

            return workflow;
        } catch (error) {
            console.error("Error fetching public workflow by slug:", error);
            const errorMessage =
                error instanceof Error ? error.message : "Failed to fetch public workflow";
            set(
                { workflow: null, workflowError: errorMessage, workflowLoading: false },
                false,
                "fetchPublicWorkflowBySlug:error"
            );

            if (onError) onError(new Error(errorMessage));
        }
    },

    fetchWorkflowById: async (id: string) => {
        try {
            set({ workflowLoading: true, workflowError: null }, false, "fetchWorkflowById:start");
            const snapshot = await getDoc(doc(db, "workflows", id));
            const workflow = { ...snapshot.data(), id: snapshot.id };
            set({ workflow }, false, "fetchWorkflowById:success");
        } catch (error) {
            console.error("Error fetching workflow by ID:", error);
            const errorMessage = `Failed to fetch workflow ${error instanceof Error ? error.message : ""}`;
            set(
                { workflow: null, workflowError: errorMessage, workflowLoading: false },
                false,
                "fetchWorkflowById:error"
            );
        } finally {
            set({ workflowLoading: false }, false, "fetchWorkflowById:end");
        }
    },

    fetchWorkflowToken: async (workspaceId: string, api: any) => {
        const workflow = get().workflow;
        if (!workflow?.id || !workspaceId) {
            set({ workflowToken: null, tokenError: null }, false, "fetchWorkflowToken:noWorkflow");
            return;
        }

        set({ tokenLoading: true, tokenError: null }, false, "fetchWorkflowToken:start");

        try {
            // Use the existing workflows API endpoint for getting tokens
            const response = await api.core.getWorkflowToken(workflow.id, workspaceId);
            const token = response.token || response;
            set({ workflowToken: token, tokenLoading: false }, false, "fetchWorkflowToken:success");
        } catch (err) {
            console.error("Failed to fetch workflow token:", err);
            const errorMessage = `Failed to fetch workflow token ${err instanceof Error ? err.message : ""}`;
            set(
                { tokenError: errorMessage, workflowToken: null, tokenLoading: false },
                false,
                "fetchWorkflowToken:error"
            );
        }
    },

    clearWorkflowState: () => {
        set(
            {
                workflow: null,
                creatingRapidWorkflow: false,
                workflowLoading: false,
                workflowError: null,
                workflowToken: null,
                tokenLoading: false,
                tokenError: null
            },
            false,
            "clearWorkflowState"
        );
    }
});
